/**
 * @file host-state-machine.ts
 *
 * Host-side state machine for the embed.
 *
 * Owns the canonical, namespaced viewer state (`host` + `author`),
 * the commit lifecycle (microtask + rAF coalesce), the per-pass id
 * and cryptographic nonce, and the rules for which inbound worker
 * messages count as "the current pass" vs. "stale and droppable".
 *
 * The runner delegates here so the state-management concerns aren't
 * tangled with worker/transport plumbing. See `docs/plans/state-management.md`
 * for the four invariants this implements (full reload on change, no
 * race, parent-emit, drilldown-stateful).
 *
 * ## Why a separate class
 *
 * - Single source of truth: every `setState` / `replaceState` /
 *   inbound `state_set` flows through one object that decides when
 *   to bump `passId`, when to mint a nonce, when to coalesce, when
 *   to fire `onStateChange` (commit, NOT intent — `liveState` on the
 *   dashboard must mirror what the embed actually rendered).
 * - Testable in isolation: this module imports no Worker / HTTP /
 *   DOM dependencies, so a unit test can drive the lifecycle end to
 *   end and assert the wire payloads.
 *
 * ## Namespaced state
 *
 * `host` is what the embedding page sets via `setState`/`replaceState`.
 * `author` is what insight code sets via `sbi.state(k, v)` inside the
 * worker. The worker sees `{ ...host, ...author }` (author wins on
 * collision) — collisions are diagnosed once per runner via the
 * `host_author_state_collision` `lucent-diagnostic` code.
 *
 * Host-side `setState` SHALLOW MERGES into `host`. `undefined` deletes
 * the key; `null` sets to null. `replaceState` replaces wholesale.
 * Worker `state_set` writes a single key into `author`.
 *
 * ## Commit lifecycle
 *
 * 1. Mutation → `scheduleCommit()` — sets a microtask, then an rAF
 *    on top, so same-tick bursts coalesce into a single rerun. The
 *    rAF is what catches programmatic flurries that cross macrotask
 *    boundaries (e.g. a host calling `setState({a:1})` then `setState({b:2})`
 *    via two separate `await fetch()` resolves — both still land in
 *    the same animation frame and merge into one pass).
 * 2. On flush: bump `passId`, mint a fresh nonce, call out via
 *    `onCommit({state, passId, nonce})`. The runner forwards to the
 *    Worker as the `rerun` message and aborts in-flight HTTP.
 * 3. Worker eventually echoes `pass_committed{passId, nonce}` once
 *    every patcher has settled. The machine matches on BOTH passId
 *    AND nonce; a mismatch on either is silently dropped. The
 *    runner uses `acceptCommit()` to verify before firing
 *    `onStateChange` outward.
 *
 * Pass cancellation: a new mutation while pass N is in flight bumps
 * to passId N+1. The runner aborts pass N's HTTP, and pass N's eventual
 * `pass_committed` mismatches on `passId` → silently dropped. No
 * `onStateChange` fires until pass N+1 actually commits.
 *
 * ## Forging defence
 *
 * Author code in the Worker can call `postMessage(...)` if the
 * validator misses it — `self` is banned, but a bareword resolves to
 * `self.postMessage`. The new validator banlist closes that surface;
 * the nonce is belt-and-braces. Author code can guess `passId`
 * (small integer); it cannot guess a `crypto.randomUUID()` nonce.
 */

import type { NamespacedState } from "./protocol.ts";

/**
 * Inputs the state machine reaches out to. The machine itself does
 * no I/O — these callbacks let the runner translate state-machine
 * decisions into worker postMessage / event emission.
 */
export interface HostStateMachineOptions {
  /**
   * Called when a coalesced commit window flushes. The runner posts
   * `{ type: "rerun", passId, nonce, state }` to the worker and
   * aborts in-flight HTTP.
   *
   * Not called for the very first kickoff (`firstReadyAck`) — that
   * goes through `prepareFirstAck()` which produces the same
   * envelope but as a `ready_ack` message instead of a `rerun`.
   */
  onCommit(payload: {
    passId: number;
    nonce: string;
    state: NamespacedState;
  }): void;

  /**
   * Called on every passId bump, regardless of commit/cancel
   * outcome. Pairs with `onPassEnd` so the runner can flip
   * `aria-busy` symmetrically.
   */
  onPassStart?(): void;

  /**
   * Called when a pass terminates — either it committed (matched
   * nonce on `pass_committed`) or it errored. NOT called when a
   * pass is silently cancelled by a newer pass; the newer pass's
   * eventual end fires for both.
   */
  onPassEnd?(status: "committed" | "errored"): void;

  /**
   * Called after a commit-matched `pass_committed` — the runner
   * uses this to emit the outward `state-change` DOM event. The
   * payload is a fresh namespaced object the caller can stash
   * directly.
   */
  onCommitted?(state: Readonly<NamespacedState>): void;

  /**
   * UUID minter. Default is `crypto.randomUUID()`; tests can inject
   * a deterministic sequence so wire-shape assertions are stable.
   */
  randomUUID?(): string;

  /**
   * Microtask scheduler. Default is `queueMicrotask`. Test seam.
   */
  microtask?(fn: () => void): void;

  /**
   * Animation-frame scheduler. Default is `requestAnimationFrame`
   * when available, falls back to `setTimeout(..., 0)` in non-DOM
   * contexts (jsdom / happy-dom worker tests). Test seam.
   */
  rafSchedule?(fn: () => void): void;
}

/**
 * Internal pass record. Tracked so a late `pass_committed` from a
 * pass that errored / was cancelled is recognisably stale.
 */
type PassStatus = "pending" | "committed" | "errored" | "cancelled";

export class HostStateMachine {
  // Public for the runner to spread on first kickoff; private setters
  // gate every mutation through the commit pipeline.
  private hostState: Record<string, unknown> = {};
  private authorState: Record<string, unknown> = {};
  /**
   * Embed-token-pinned values. Seeded once via `seed({locked})` (or
   * `setLocked`) from the source-fetch response and never mutated
   * thereafter — the consumer page cannot reach this surface and
   * worker `state_set` writes go to `authorState`. The worker merges
   * `{...host, ...author, ...locked}` so a `setState` for a locked
   * key is silently shadowed; the server-side query handler also
   * overrides matching positional params at execute time.
   */
  private lockedState: Record<string, unknown> = {};

  private passId = 0;
  private nonce = "";
  private lastCommittedPassId = -1;

  private commitScheduled = false;
  private firstCommitFired = false;

  /**
   * Per-pass status table. Bounded by N (one entry per pass). We
   * never read entries older than `passId - 1` so the leak surface
   * is tiny; entries are still pruned defensively when passing
   * `committed`/`errored`/`cancelled`.
   */
  private passStatus = new Map<number, PassStatus>();

  constructor(private readonly opts: HostStateMachineOptions) {}

  // ── Public API surfaced to the runner ───────────────────────────

  /**
   * Snapshot the current namespaced state. Returned object is a
   * fresh shallow copy; callers can mutate without affecting the
   * machine's internal maps.
   */
  snapshot(): NamespacedState {
    return {
      host: { ...this.hostState },
      author: { ...this.authorState },
      locked: { ...this.lockedState },
    };
  }

  /** Currently-committed pass id, or -1 if nothing has committed yet. */
  getLastCommittedPassId(): number {
    return this.lastCommittedPassId;
  }

  /** The id assigned to the next commit. Used by tests + the runner. */
  getCurrentPassId(): number {
    return this.passId;
  }

  /**
   * Seed initial state before the first kickoff. Both halves
   * default to `{}`. The runner calls this once at construction
   * time so first-paint reflects the embedder's `initialState` /
   * `initialAuthorState` props.
   */
  seed(initial: Partial<NamespacedState>): void {
    if (initial.host) this.hostState = { ...initial.host };
    if (initial.author) this.authorState = { ...initial.author };
    if (initial.locked) this.lockedState = { ...initial.locked };
  }

  /**
   * Set (or replace) the locked namespace. Called once by the runner
   * after the source-fetch response lands, with the token's
   * `locked_filters` map. Does NOT schedule a commit — the seed
   * runs before the first `ready_ack`, so the first pass picks the
   * values up via `snapshot()`. Idempotent: safe to call with the
   * same values multiple times during a runner restart.
   */
  setLocked(locked: Record<string, unknown>): void {
    this.lockedState = { ...locked };
  }

  /**
   * Build the envelope the runner ships on the very first
   * `ready_ack`. Distinct from a normal commit: there's no prior
   * pass to cancel, no `cancelInFlight` to call, no `onPassStart`
   * — the worker hasn't started a pass yet. This still bumps
   * `passId` so subsequent `pass_committed` echoes can be matched.
   */
  prepareFirstAck(): { passId: number; nonce: string; state: NamespacedState } {
    this.passId += 1;
    this.nonce = this.mintNonce();
    this.passStatus.set(this.passId, "pending");
    this.firstCommitFired = true;
    return { passId: this.passId, nonce: this.nonce, state: this.snapshot() };
  }

  /**
   * Shallow-merge a partial into `host`. `undefined` deletes a key;
   * `null` writes null. Schedules a coalesced commit.
   */
  setHost(partial: Record<string, unknown>): void {
    for (const k of Object.keys(partial)) {
      const v = partial[k];
      if (v === undefined) {
        delete this.hostState[k];
      } else {
        this.hostState[k] = v;
      }
    }
    this.scheduleCommit();
  }

  /** Replace `host` wholesale. Schedules a coalesced commit. */
  replaceHost(state: Record<string, unknown>): void {
    this.hostState = { ...state };
    this.scheduleCommit();
  }

  /**
   * Apply an inbound `state_set` from the worker. Writes the key
   * into `author` (the worker's side of the namespace) and
   * schedules a commit. Same coalesce window as host-side
   * mutations, so a burst of setters within one animation frame
   * lands as a single `rerun`.
   */
  applyAuthorSet(key: string, value: unknown): void {
    this.authorState[key] = value;
    this.scheduleCommit();
  }

  /**
   * Verify an inbound `pass_committed` message. Returns the
   * committed namespaced state on match (so the runner can emit
   * `onStateChange`), null when the message is stale or forged.
   *
   * The match requires BOTH the passId and the nonce to line up
   * with the most recent rerun. A bare-`postMessage` forgery from
   * author code can guess the passId (small integer) but cannot
   * guess a `crypto.randomUUID()` nonce.
   */
  acceptCommit(msg: { passId: number; nonce: string }): NamespacedState | null {
    if (msg.passId !== this.passId) return null;
    if (msg.nonce !== this.nonce) return null;
    if (this.passStatus.get(msg.passId) !== "pending") return null;
    this.passStatus.set(msg.passId, "committed");
    this.lastCommittedPassId = msg.passId;
    const state = this.snapshot();
    this.opts.onCommitted?.(state);
    this.opts.onPassEnd?.("committed");
    this.gcPasses();
    return state;
  }

  /**
   * Record that the current pass surfaced an error. Mirrors
   * `acceptCommit` for the error path: state is NOT rolled back,
   * but `onPassEnd("errored")` fires so the runner can flip
   * `aria-busy` symmetrically. The next mutation drives a fresh
   * commit at `passId + 1` as normal.
   */
  noteError(): void {
    if (this.passStatus.get(this.passId) === "pending") {
      this.passStatus.set(this.passId, "errored");
      this.opts.onPassEnd?.("errored");
      this.gcPasses();
    }
  }

  /**
   * True if the inbound message's `passId` matches the current
   * pass. Used by the runner to drop stale `render` / `render_patch`
   * messages from cancelled passes. `query_result` / `query_error`
   * always pass through (the host's `printPendingQueries` counter
   * was incremented on dispatch and must be decremented on response
   * regardless of pass cancellation).
   */
  isCurrentPass(passId: number | undefined): boolean {
    if (passId === undefined) return true; // bootstrap-stage / global-error message
    return passId === this.passId;
  }

  // ── Internal scheduling ────────────────────────────────────────

  private scheduleCommit(): void {
    if (this.commitScheduled) return;
    this.commitScheduled = true;
    const rafSchedule =
      this.opts.rafSchedule ?? defaultRafSchedule;
    const microtask = this.opts.microtask ?? queueMicrotask;
    microtask(() => {
      rafSchedule(() => this.flushCommit());
    });
  }

  private flushCommit(): void {
    if (!this.commitScheduled) return; // defensive — flush twice should no-op
    this.commitScheduled = false;
    // Mark the prior pass cancelled (if it was still pending) so a
    // late `pass_committed` from it gets recognisably stale.
    if (this.passStatus.get(this.passId) === "pending") {
      this.passStatus.set(this.passId, "cancelled");
    }
    this.passId += 1;
    this.nonce = this.mintNonce();
    this.passStatus.set(this.passId, "pending");
    this.opts.onPassStart?.();
    this.opts.onCommit({
      passId: this.passId,
      nonce: this.nonce,
      state: this.snapshot(),
    });
  }

  private mintNonce(): string {
    const minter = this.opts.randomUUID ?? defaultRandomUUID;
    return minter();
  }

  /**
   * Prune passStatus entries older than the current pass. Cheap
   * defensive cleanup — without it, a long-lived runner that has
   * cycled through tens of thousands of passes accumulates
   * status entries that nothing reads.
   */
  private gcPasses(): void {
    const keep = this.passId;
    for (const id of this.passStatus.keys()) {
      if (id < keep - 1) this.passStatus.delete(id);
    }
  }

  // Test seam — exposed only for unit tests; not part of the
  // documented surface.
  /** @internal */
  _hasPendingCommit(): boolean {
    return this.commitScheduled;
  }
}

/** Default rAF scheduler with a non-DOM fallback. */
function defaultRafSchedule(fn: () => void): void {
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(fn);
  } else {
    setTimeout(fn, 0);
  }
}

/** Default nonce minter; tests can inject. */
function defaultRandomUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Non-crypto fallback for environments without Web Crypto (older
  // jsdom). Still 128 bits of entropy via Math.random — adequate for
  // the test seam; production browsers always have crypto.randomUUID.
  let s = "";
  for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}
